<script setup lang="ts">
import { Pause, Play } from 'lucide-vue-next';
import type { HomeOptionwayAirlinesProperties } from '@optionway/models';

const properties = defineProps<HomeOptionwayAirlinesProperties>();

const descriptionId = useId();
const prefix = 'components.home.optionwayAirlines';
const isPaused = ref(false);

const { $appConfig } = useNuxtApp();
const config = $appConfig.app?.components?.homeOptionwayAirlines;
const componentConfig = computed<HomeOptionwayAirlinesProperties>(() => ({
  numberOfAirlines: properties.numberOfAirlines ?? config?.numberOfAirlines ?? 350,
  title: properties.title ?? config?.title,
  description: properties.description ?? config?.description,
  topAirlines: properties.topAirlines ?? config?.topAirlines ?? [],
  bottomAirlines: properties.bottomAirlines ?? config?.bottomAirlines ?? [],
}));

const expandedTopAirlines = computed(() => expandForInfiniteCarousel(componentConfig.value.topAirlines ?? []));
const expandedBottomAirlines = computed(() => expandForInfiniteCarousel(componentConfig.value.bottomAirlines ?? []));
</script>

<template>
  <section
    v-if="expandedTopAirlines.length > 0 || expandedBottomAirlines.length > 0"
    aria-labelledby="airlines-title"
    class="grid w-full grid-cols-1 justify-items-center gap-10"
  >
    <div class="flex flex-col items-center gap-6 px-4 text-center md:px-6">
      <h2
        id="airlines-title"
        class="text-2xl font-semibold leading-tight text-text-primary-900 md:text-3xl"
      >
        <span class="text-brand-secondary-700">
          {{ $t(`${prefix}.titleNumber`, { number: componentConfig.numberOfAirlines }) }}
        </span>
        {{ useLocalized(componentConfig.title) }}
      </h2>
      <p class="text-sm font-normal text-text-secondary-600">
        {{ $t(`${prefix}.tagline`) }}
      </p>
    </div>
    <span
      :id="descriptionId"
      class="sr-only"
    >
      {{ useLocalized(componentConfig.description) }}
    </span>

    <div
      class="group flex w-full flex-col gap-3"
      :aria-describedby="descriptionId"
    >
      <div class="relative w-full overflow-hidden">
        <div
          class="pointer-events-none absolute inset-y-0 left-0 z-10 w-12 bg-gradient-to-r from-white to-transparent motion-reduce:hidden md:w-20"
        />
        <div
          class="pointer-events-none absolute inset-y-0 right-0 z-10 w-12 bg-gradient-to-l from-white to-transparent motion-reduce:hidden md:w-20"
        />

        <!-- The loop renders each row twice for a seamless wrap: assistive technologies read the first copy only. -->
        <div class="flex flex-col gap-6 motion-reduce:gap-3">
          <div
            :class="[
              'flex w-max motion-reduce:w-full motion-reduce:animate-none',
              isPaused ? 'animate-none' : 'animate-slider',
            ]"
          >
            <div
              v-for="iteration in 2"
              :key="`top-${iteration}`"
              role="list"
              :aria-hidden="iteration === 2 || undefined"
              :class="[
                'flex gap-3 pr-3 motion-reduce:flex-wrap motion-reduce:justify-center motion-reduce:px-4 md:motion-reduce:px-6',
                { 'motion-reduce:hidden': iteration === 2 },
              ]"
            >
              <div
                v-for="(airline, index) in expandedTopAirlines"
                :key="`top-${iteration}-${index}`"
                role="listitem"
                class="flex h-11 w-24 shrink-0 items-center justify-center rounded-lg bg-background p-2 md:h-14 md:w-28"
              >
                <UiCustomAirlineLogo
                  :iata-code="airline.iataCode"
                  :name="airline.name"
                  class="max-h-5 w-auto max-w-full md:max-h-7"
                />
              </div>
            </div>
          </div>

          <div
            :class="[
              'flex w-max motion-reduce:w-full motion-reduce:animate-none',
              isPaused ? 'animate-none' : 'animate-slider-reverse',
            ]"
          >
            <div
              v-for="iteration in 2"
              :key="`bottom-${iteration}`"
              role="list"
              :aria-hidden="iteration === 2 || undefined"
              :class="[
                'flex gap-3 pr-3 motion-reduce:flex-wrap motion-reduce:justify-center motion-reduce:px-4 md:motion-reduce:px-6',
                { 'motion-reduce:hidden': iteration === 2 },
              ]"
            >
              <div
                v-for="(airline, index) in expandedBottomAirlines"
                :key="`bottom-${iteration}-${index}`"
                role="listitem"
                class="flex h-11 w-24 shrink-0 items-center justify-center rounded-lg bg-background p-2 md:h-14 md:w-28"
              >
                <UiCustomAirlineLogo
                  :iata-code="airline.iataCode"
                  :name="airline.name"
                  class="max-h-5 w-auto max-w-full md:max-h-7"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="flex justify-end px-4 motion-reduce:hidden md:px-6">
        <button
          type="button"
          class="inline-flex size-8 items-center justify-center rounded-full border border-gray-200 bg-card opacity-0 shadow-sm transition-all hover:bg-gray-50 focus:opacity-100 group-hover:opacity-100"
          :aria-label="isPaused ? $t(`${prefix}.play`) : $t(`${prefix}.pause`)"
          @click="isPaused = !isPaused"
        >
          <Pause
            v-if="!isPaused"
            class="size-3.5 text-text-primary-900"
            :stroke-width="2"
            aria-hidden="true"
          />
          <Play
            v-else
            class="size-3.5 text-text-primary-900"
            :stroke-width="2"
            aria-hidden="true"
          />
        </button>
      </div>
    </div>
  </section>
</template>
